import { useState } from 'react';
import { Link } from 'react-router-dom';
import { completeObligation, createObligation, KEEPALIVE, listObligations, updateObligation } from '../lifeos/api.js';
import { daysBetween, dueChip, humanize, money, ymdInHomeZone } from '../lifeos/format.js';
import FormSheet, { type Field } from '../lifeos/FormSheet.js';
import { cents, intList, options, text, toValues } from '../lifeos/forms.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import { useToast } from '../lifeos/toast.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Button, EmptyState, Group, IconButton, Loader, Page, Row, Fold, Meta, useParamSetter, useSphereLens, When } from '../ui/index.js';
import type { Obligation, ObligationKind, ObligationRecurrence, Sphere } from '../lifeos/types.js';

const KINDS: ObligationKind[] = ['bill', 'tax', 'renewal', 'registration', 'insurance', 'license', 'domain', 'subscription', 'other'];
const RECURRENCES: ObligationRecurrence[] = ['none', 'monthly', 'quarterly', 'yearly'];

/** Where a derived obligation is edited (spec §3.3: its dates belong to the source row). */
export function derivedSource(autoKey: string | null): { label: string; to: string; page: string } | null {
  if (autoKey?.startsWith('document:')) return { label: 'from a document', to: '/documents', page: 'Documents' };
  if (autoKey?.startsWith('service:')) return { label: 'from a service', to: '/services', page: 'Services' };
  if (autoKey?.startsWith('belonging_warranty:')) return { label: 'from a warranty', to: '/belongings', page: 'Belongings' };
  return null;
}

/** An expiry is renewed, not "done". */
export function doneLabel(o: Pick<Obligation, 'kind'>): string {
  return o.kind === 'document_expiry' || o.kind === 'warranty' ? 'Renewed' : 'Done';
}

/** How many rows each group shows before "Show N more". */
const GROUP_SHOWN: Record<string, number> = { Overdue: 5, 'Next 30 days': 5, Later: 3 };

/**
 * Obligations (`/obligations`): bills, taxes and renewals by how soon they're
 * due. Done is the one row action (button or full right swipe, 5-second
 * Undo); tapping the row edits it. Rows derived from a document, service or
 * warranty change their dates at the source.
 */
export default function Obligations(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const sphere = params.get('sphere');
  const spheres = useSpheres();
  const lens = useSphereLens(spheres, sphere, (k) => setParam('sphere', k));
  const list = useLoad(() => listObligations({ sphere: sphere ?? undefined }), [sphere]);
  const toast = useToast();
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<Obligation | 'new' | null>(null);
  const today = ymdInHomeZone(new Date());

  function done(o: Obligation): void {
    const unhide = (): void =>
      setHidden((s) => {
        const n = new Set(s);
        n.delete(o.id);
        return n;
      });
    setHidden((s) => new Set(s).add(o.id));
    toast.defer({
      text: `${doneLabel(o)}: ${o.title}`,
      // dueOn pins the period shown; a 409 "due date has moved" reloads.
      commit: () => completeObligation(o.id, { dueOn: o.dueOn }, KEEPALIVE),
      onUndo: unhide,
      onDone: () => void list.reload().finally(unhide),
      onError: () => void list.reload().finally(unhide),
      failText: `Couldn't complete ${o.title}`,
    });
  }

  const due = list.data?.filter((o) => o.active && !hidden.has(o.id) && daysBetween(today, o.dueOn) <= 30) ?? [];
  const dueSum = due.reduce((s, o) => s + (o.amountCents ?? 0), 0);

  return (
    <Page
      title="Obligations"
      sub={list.data ? (due.length ? `${due.length} due in 30 days${dueSum ? ` · ${money(dueSum)}` : ''}` : 'Nothing due this month') : undefined}
      actions={
        <>
          {lens.button}
          <IconButton icon="plus" label="Add" onClick={() => setEditing('new')} />
        </>
      }
    >
      {lens.chip && <div className="ui-chips">{lens.chip}</div>}
      <Loader state={list}>
        {(items) => {
          const rows = items.filter((o) => !hidden.has(o.id)).sort((a, b) => a.dueOn.localeCompare(b.dueOn));
          const active = rows.filter((o) => o.active);
          const groups: [string, Obligation[]][] = [
            ['Overdue', active.filter((o) => o.dueOn < today)],
            ['Next 30 days', active.filter((o) => o.dueOn >= today && daysBetween(today, o.dueOn) <= 30)],
            ['Later', active.filter((o) => daysBetween(today, o.dueOn) > 30)],
          ];
          const paused = rows.filter((o) => !o.active);
          if (rows.length === 0)
            return (
              <EmptyState
                icon="receipt"
                title="No obligations yet."
                hint="Add bills, renewals and tax dates."
                action={
                  <Button variant="secondary" icon="plus" onClick={() => setEditing('new')}>
                    Add one
                  </Button>
                }
              />
            );
          return (
            <>
              {groups.map(([label, os]) => (
                <Group key={label} header={label} label={label}>
                  {os.length > 0 && (
                    <Fold
                      items={os}
                      n={GROUP_SHOWN[label] ?? 5}
                      render={(o) => <ObligationRow key={o.id} o={o} today={today} color={sphereColor(spheres, o.sphereKey)} onDone={() => done(o)} onEdit={() => setEditing(o)} />}
                    />
                  )}
                </Group>
              ))}
              <Group header="Paused" label="Paused">
                {paused.length > 0 && (
                  <Fold
                    items={paused}
                    n={0}
                    noun="paused"
                    render={(o) => <ObligationRow key={o.id} o={o} today={today} color={sphereColor(spheres, o.sphereKey)} onEdit={() => setEditing(o)} />}
                  />
                )}
              </Group>
            </>
          );
        }}
      </Loader>
      {lens.sheet}
      {editing && (
        <ObligationSheet
          o={editing === 'new' ? null : editing}
          spheres={spheres}
          onClose={() => setEditing(null)}
          onSaved={() => void list.reload()}
        />
      )}
    </Page>
  );
}

function ObligationRow({
  o,
  today,
  color,
  onDone,
  onEdit,
}: {
  o: Obligation;
  today: string;
  color: string;
  onDone?: () => void;
  onEdit: () => void;
}): JSX.Element {
  const source = derivedSource(o.autoKey);
  const estimate = o.amountRule?.kind === 'tax_set_aside';
  const chip = dueChip(o.dueOn, today);
  // The date leads the sub line; the amount is the row's one number; where a
  // derived row comes from is said in words.
  return (
    <Row
      className="la-ob"
      onClick={onEdit}
      ariaLabel={`Edit ${o.title}`}
      tone={o.active ? 'default' : 'muted'}
      title={o.title}
      sub={
        <>
          {chip && <When tone={chip.tone}>{chip.label}</When>}
          <Meta color={color}>
            {humanize(o.kind)}
            {o.amountCents === null && estimate && ' · set-aside estimate'}
            {source && ` · ${source.label}`}
          </Meta>
        </>
      }
      detail={o.amountCents !== null ? `${estimate ? 'est. ' : ''}${money(o.amountCents)}` : undefined}
      chevron={false}
      trailing={
        onDone ? (
          <Button size="sm" variant="secondary" onClick={onDone} aria-label={`${doneLabel(o)}: ${o.title}`}>
            {doneLabel(o)}
          </Button>
        ) : undefined
      }
      swipe={onDone ? { leading: { label: doneLabel(o), icon: 'check', tone: 'good', onAction: onDone } } : undefined}
    />
  );
}

function ObligationSheet({
  o,
  spheres,
  onClose,
  onSaved,
}: {
  o: Obligation | null;
  spheres: Sphere[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const source = o ? derivedSource(o.autoKey) : null;
  const derived = source !== null;
  const editable: Field[] = [
    { key: 'title', label: 'Title', required: true, wide: true },
    { key: 'kind', label: 'Kind', kind: 'select', options: options(KINDS, humanize) },
    { key: 'dueOn', label: 'Next due', kind: 'date', required: true },
    { key: 'recurrence', label: 'Repeats', kind: 'select', options: options(RECURRENCES, (r) => (r === 'none' ? 'Never' : humanize(r))) },
    { key: 'amountCents', label: 'Amount ($)', kind: 'money' },
    { key: 'sphereKey', label: 'Sphere', kind: 'select', options: spheres.map((s) => ({ value: s.key, label: s.name })) },
  ];
  const always: Field[] = [
    { key: 'leadDays', label: 'Remind, days before', placeholder: '30, 7, 0', hint: 'comma separated; blank uses the default for the kind' },
    { key: 'active', label: 'Active', kind: 'checkbox' },
    { key: 'notes', label: 'Notes', kind: 'textarea' },
  ];
  const fields = derived ? always : o ? [...editable, ...always] : [...editable, always[0]!, always[2]!];
  const initial = o
    ? toValues({ ...o, active: o.active }, ['amountCents'])
    : { kind: 'bill', recurrence: 'none', sphereKey: 'personal', title: '', dueOn: '', amountCents: '', leadDays: '', notes: '' };

  return (
    <FormSheet
      title={o ? (derived ? `${o.title} (${source.label})` : `Edit ${o.title}`) : 'Add an obligation'}
      intro={
        source ? (
          <Link className="lo-link" to={source.to}>
            Change dates in {source.page} →
          </Link>
        ) : undefined
      }
      fields={fields}
      initial={initial}
      submitLabel={o ? 'Save' : 'Add'}
      onClose={onClose}
      onSubmit={async (v) => {
        const leadDays = intList(v.leadDays, 'Remind, days before');
        const common = { notes: text(v.notes), ...(leadDays ? { leadDays } : {}) };
        if (derived && o) {
          await updateObligation(o.id, { ...common, active: v.active === 'true' });
        } else {
          const body = {
            title: text(v.title)!,
            kind: v.kind as ObligationKind,
            dueOn: v.dueOn!,
            recurrence: v.recurrence as ObligationRecurrence,
            amountCents: cents(v.amountCents, 'Amount'),
            sphereKey: v.sphereKey || undefined,
            ...common,
          };
          if (o) await updateObligation(o.id, { ...body, active: v.active === 'true' });
          else await createObligation(body);
        }
        onSaved();
      }}
    />
  );
}
